import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Calendar, Zap } from 'lucide-react';
import ContactForm from '../components/forms/ContactForm';

const projects = [
  {
    title: 'Энергоснабжение завода металлоконструкций',
    description: 'Поставка и монтаж трансформаторов ТСЛ-1000/10/0,4 для обеспечения надежного энергоснабжения производственных линий.',
    location: 'г. Казань',
    date: 'Март 2024',
    equipment: ['2× ТСЛ-1000/10/0,4', 'КТПН-2×1000/10/0,4'],
    results: [
      'Снижение потерь электроэнергии на 8%',
      'Повышение надежности энергоснабжения',
      'Сокращение эксплуатационных расходов'
    ],
    image: 'https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
  },
  {
    title: 'Модернизация подстанции логистического центра',
    description: 'Комплексная поставка КТПН-2×630/10/0,4 с трансформаторами ТСЗ для бесперебойного питания складского комплекса.',
    location: 'Московская область',
    date: 'Февраль 2024',
    equipment: ['2× ТСЗ-630/10/0,4', 'КТПН-2×630/10/0,4', 'КСО-366'],
    results: [
      'Увеличение мощности на 40%',
      'Обеспечение резервирования',
      'Автоматизация управления'
    ],
    image: 'https://images.pexels.com/photos/2760241/pexels-photo-2760241.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
  },
  {
    title: 'Энергообеспечение строительства ЖК',
    description: 'Поставка комплекта КСО и трансформаторов ТСЗ для питания строительной площадки и последующей эксплуатации ЖК.',
    location: 'г. Санкт-Петербург',
    date: 'Январь 2024',
    equipment: ['4× ТСЗ-1000/10/0,4', 'КСО-366 (10 ячеек)'],
    results: [
      'Оптимизация затрат на оборудование',
      'Сокращение сроков монтажа',
      'Повышение безопасности'
    ],
    image: 'https://images.pexels.com/photos/2138126/pexels-photo-2138126.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
  }
];

export default function ProjectsPage() {
  return (
    <>
      <Helmet>
        <title>Проекты - от заводов до подстанций | Проминнова</title>
        <meta name="description" content="Реализованные проекты компании Проминнова: поставка и монтаж трансформаторов ТСЛ и ТСЗ, КТПН, КСО для промышленных и гражданских объектов." />
      </Helmet>

      <div className="bg-primary-50 py-12">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Наши проекты – от заводов до подстанций
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            За годы работы мы успешно реализовали сотни проектов по поставке трансформаторного оборудования для различных объектов
          </p>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid grid-cols-1 gap-12 mb-16">
          {projects.map((project, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="h-full">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {project.location}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {project.date}
                    </div>
                  </div>
                  
                  <h2 className="text-2xl font-bold mb-4">{project.title}</h2>
                  <p className="text-gray-600 mb-6">{project.description}</p>
                  
                  <div className="mb-6">
                    <h3 className="font-semibold mb-2">Оборудование:</h3>
                    <ul className="space-y-2">
                      {project.equipment.map((item, idx) => (
                        <li key={idx} className="flex items-center text-gray-600">
                          <Zap className="w-4 h-4 text-primary-600 mr-2" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-2">Результаты:</h3>
                    <ul className="space-y-2">
                      {project.results.map((result, idx) => (
                        <li key={idx} className="flex items-start text-gray-600">
                          <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 mt-2"></div>
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-4">Реализуем ваш проект</h2>
              <p className="text-gray-600 mb-6">
                Оставьте заявку, и мы поможем подобрать оптимальное решение для вашего объекта с учетом всех технических требований.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                  <span>Бесплатный расчет стоимости</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                  <span>Техническая консультация</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                  <span>Индивидуальный подход</span>
                </div>
              </div>
            </div>
            <div>
              <ContactForm buttonText="Обсудить проект" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}